import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronRight, Calendar } from 'lucide-react';
import IconHighlight from '../../../../../components/IconHighlight';
import { handleKeyboardClick, truncateText } from '../../../../../utils';
import { NOTE_ICON_CONFIG, NOTES_CARD_MAX_CHARACTERS, NOTES_METADATA_ICONS } from '../../constants';

/**
 * Single note list item. Expanded shows description + metadata on hover.
 * Compressed shows icon, title (truncated), chevron only.
 */
const NotesListItem = ({ item, isCompressed = false, onItemClick = undefined }) => {
  const hasMetadata = useMemo(() => {
    const m = item.metadata;
    return (
      item.author ||
      (m?.commentsCount > 0) ||
      (m?.attachmentsCount > 0) ||
      (m?.tasksCount > 0)
    );
  }, [item.author, item.metadata]);

  const handleClick = useCallback(() => {
    onItemClick?.(item);
  }, [item, onItemClick]);

  const dueTagText = item.dueDate ? `Due ${item.dueDate}` : '';

  const containerClass = [
    'bg-surface-muted/50 border border-edge rounded-lg flex flex-col gap-2 w-full cursor-pointer hover:bg-brand-50/90 dark:hover:bg-brand-950/25 transition-colors',
    isCompressed ? 'p-2' : 'p-3',
    !isCompressed ? 'group' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-compressed={isCompressed || undefined}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      className={containerClass}
      onKeyDown={(event) => handleKeyboardClick(event, handleClick)}
    >
      {/* Main row */}
      <div className="flex items-center justify-between gap-1 w-full">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <IconHighlight
            icon={NOTE_ICON_CONFIG.icon}
            bgColor={NOTE_ICON_CONFIG.bgColor}
            iconColor={NOTE_ICON_CONFIG.iconColor}
          />
          <div className={`flex-1 min-w-0 ${isCompressed ? 'overflow-hidden' : ''}`}>
            <span
              className={[
                'text-xs leading-5 font-medium text-content',
                isCompressed ? 'block overflow-hidden text-ellipsis whitespace-nowrap' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {truncateText(item.title, NOTES_CARD_MAX_CHARACTERS)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {!isCompressed && dueTagText && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 text-xs rounded-full">
              <Calendar size={11} />
              {dueTagText}
            </span>
          )}
          {!isCompressed && item.timeAgo && (
            <span className="text-xs font-medium text-content-muted">{item.timeAgo}</span>
          )}
          <button
            type="button"
            aria-label="View details"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="inline-flex items-center justify-center w-7 h-7 rounded text-content-faint hover:bg-surface-hover transition-colors"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Description (expanded only) */}
      {!isCompressed && item.description && (
        <div className="flex-1 min-w-0">
          <span className="text-xs leading-5 text-content-muted">
            {truncateText(item.description, NOTES_CARD_MAX_CHARACTERS)}
          </span>
        </div>
      )}

      {/* Metadata bar — revealed on hover via Tailwind group */}
      {!isCompressed && hasMetadata && (
        <div className="w-fit bg-surface-muted/70 border border-edge rounded-lg flex items-center gap-2 flex-wrap max-h-0 overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover:max-h-[50px] group-hover:opacity-100 group-hover:py-1 group-hover:px-2 group-hover:border">
          {item.author && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-xs font-medium text-content-secondary">{item.author}</span>
            </div>
          )}
          {item.metadata?.commentsCount > 0 && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <NOTES_METADATA_ICONS.comments size={14} className="text-content-muted" />
              <span className="text-xs font-medium text-content-muted">{item.metadata.commentsCount}</span>
            </div>
          )}
          {item.metadata?.attachmentsCount > 0 && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <NOTES_METADATA_ICONS.attachments size={14} className="text-content-muted" />
              <span className="text-xs font-medium text-content-muted">{item.metadata.attachmentsCount}</span>
            </div>
          )}
          {item.metadata?.tasksCount > 0 && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <NOTES_METADATA_ICONS.tasks size={14} className="text-content-muted" />
              <span className="text-xs font-medium text-content-muted">{item.metadata.tasksCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

NotesListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    dueDate: PropTypes.string,
    author: PropTypes.string,
    timeAgo: PropTypes.string,
    metadata: PropTypes.shape({
      commentsCount: PropTypes.number,
      attachmentsCount: PropTypes.number,
      tasksCount: PropTypes.number,
    }),
  }).isRequired,
  isCompressed: PropTypes.bool,
  onItemClick: PropTypes.func,
};


export default React.memo(NotesListItem);
